import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { useBrief } from "@/context/BriefContext";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const NAV = [
  { to: "/work", label: "Work", testid: "site-nav-work-link" },
  { to: "/services", label: "Services", testid: "site-nav-services-link" },
  { to: "/resources", label: "Resources", testid: "site-nav-resources-link" },
  { to: "/events", label: "Events", testid: "site-nav-events-link" },
  { to: "/about", label: "About", testid: "site-nav-about-link" },
];

export function Header() {
  const { openBrief } = useBrief();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const navItem = (item, mobile = false) => (
    <NavLink
      key={item.to}
      to={item.to}
      data-testid={item.testid}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        `cv-sans uppercase text-xs tracking-[0.14em] transition-colors duration-150 ${
          mobile ? "py-3 block" : ""
        } ${isActive ? "text-[#1F1B16]" : "text-[#1F1B16]/60 hover:text-[#1F1B16]"}`
      }
    >
      {item.label}
    </NavLink>
  );

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-[#F6F2EC]/95 backdrop-blur-sm border-b border-[rgba(28,27,25,0.14)]"
    >
      <div className="cv-container flex items-center justify-between h-16">
        <Link to="/" aria-label="Copper and Vine home" className="flex items-center">
          <Wordmark />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((i) => navItem(i))}
          {user ? (
            <Link
              to="/hub"
              data-testid="site-nav-hub-link"
              className="cv-sans uppercase text-xs tracking-[0.14em] text-[#1F1B16] inline-flex items-center gap-1.5"
            >
              <User size={14} /> Hub
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="site-nav-login-link"
              className="cv-sans uppercase text-xs tracking-[0.14em] text-[#1F1B16]/60 hover:text-[#1F1B16]"
            >
              Sign in
            </Link>
          )}
          <button
            data-testid="global-brief-request-button"
            className="cv-btn-primary"
            onClick={() => {
              track("door_click", { target: "brief_request", location: "header" });
              openBrief("general");
            }}
          >
            Request the brief
          </button>
        </nav>

        <button
          className="lg:hidden p-2 -mr-2 text-[#1F1B16]"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[rgba(28,27,25,0.14)] bg-[#F6F2EC]">
          <div className="cv-container py-4">
            {NAV.map((i) => navItem(i, true))}
            {user ? (
              <Link to="/hub" data-testid="site-nav-hub-link-mobile" onClick={() => setOpen(false)} className="cv-sans uppercase text-xs tracking-[0.14em] py-3 block text-[#1F1B16]">
                The Hub
              </Link>
            ) : (
              <Link to="/login" data-testid="site-nav-login-link-mobile" onClick={() => setOpen(false)} className="cv-sans uppercase text-xs tracking-[0.14em] py-3 block text-[#1F1B16]/70">
                Sign in
              </Link>
            )}
            <button
              data-testid="global-brief-request-button-mobile"
              className="cv-btn-primary w-full mt-4"
              onClick={() => {
                setOpen(false);
                track("door_click", { target: "brief_request", location: "header_mobile" });
                openBrief("general");
              }}
            >
              Request the brief
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
